/**
 * Alpha Trade V2 Dashboard Overlay — "Neon Edge Card" Design
 * Unique: Neon glow border, accent strip, centered signal badge, hit icons (✓/✗)
 * Transparent arrow/hit badges with matching color text
 * CSS prefix: atv2-
 */
import React from "react";
import { AlphaTradeV2Dashboard } from "../../indicators/built-in/alpha-trade-v2";
import "./AlphaTradeV2Overlay.scss";

interface AlphaTradeV2OverlayProps {
  dashboard: AlphaTradeV2Dashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard: boolean;
  onToggleDashboard: () => void;
  arrowBadges: { x: number; y: number; direction: "long" | "short"; type: string; position: string }[];
}

const AlphaTradeV2Overlay: React.FC<AlphaTradeV2OverlayProps> = ({
  dashboard, symbol, visible, showDashboard, onToggleDashboard, arrowBadges,
}) => {
  if (!visible) return null;

  const d = dashboard;
  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "—");
  const fmtPL = (v: number) => (v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2));

  const isBuy = d?.signal === "BUY";
  const isSell = d?.signal === "SELL";
  const themeClass = isBuy ? "atv2-buy" : isSell ? "atv2-sell" : "atv2-neutral";

  const sigLabel = d?.signal ?? "—";
  const sigClass = isBuy ? "atv2-sig-buy" : isSell ? "atv2-sig-sell" : "atv2-sig-neutral";

  const statusClass =
    d?.status === "Target1 Hit" || d?.status === "Target2 Hit" ? "atv2-status-tp"
    : d?.status === "Stop Loss Hit" ? "atv2-status-sl"
    : d?.status === "Active" ? "atv2-status-active"
    : "";

  return (
    <>
      {/* ─── Arrow & Hit Badges ─── */}
      {arrowBadges.map((badge, i) => {
        let label = "";
        let badgeClass = "";
        if (badge.type === "signal") {
          label = badge.direction === "long" ? "Buy" : "Sell";
          badgeClass = badge.direction === "long" ? "atv2-badge--buy" : "atv2-badge--sell";
        } else if (badge.type === "tp1") {
          label = "TGT-1 ✓";
          badgeClass = "atv2-badge--tp";
        } else if (badge.type === "tp2") {
          label = "TGT-2 ✓";
          badgeClass = "atv2-badge--tp";
        } else if (badge.type === "sl") {
          label = "SL ✗";
          badgeClass = "atv2-badge--sl";
        }
        return (
          <div
            key={i}
            className={`atv2-arrow-badge ${badgeClass}`}
            style={{
              left: badge.x - 18,
              top: badge.position === "below" ? badge.y + 24 : badge.y - 38,
            }}
          >
            {label}
          </div>
        );
      })}

      <div className={`atv2-overlay ${themeClass}${showDashboard ? "" : " atv2-overlay--collapsed"}`}>
        {/* ─── Accent Strip ─── */}
        {showDashboard && <div className="atv2-accent-strip" />}

        {showDashboard && d ? (
          <>
            {/* ─── Header: Title + Toggle ─── */}
            <div className="atv2-header">
              <span className="atv2-title">ALPHA TRADE</span>
              <button className="atv2-toggle-btn" onClick={onToggleDashboard} title="Collapse">▲</button>
            </div>

            {/* ─── Price Block ─── */}
            <div className="atv2-price-block">
              <span className="atv2-symbol">{symbol}</span>
              <span className="atv2-cmp">{d.currentPrice.toFixed(2)}</span>
            </div>

            {/* ─── Signal Badge (centered) ─── */}
            <div className="atv2-signal-wrap">
              <span className={`atv2-signal-badge ${sigClass}`}>{sigLabel}</span>
            </div>

            {/* ─── Divider ─── */}
            <div className="atv2-divider" />

            {/* ─── Levels ─── */}
            <div className="atv2-levels">
              <div className="atv2-level-row">
                <span className="atv2-level-label">ENTRY</span>
                <span className="atv2-level-value">{fmt(d.entry)}</span>
              </div>
              <div className={`atv2-level-row${d.tp1Hit ? " atv2-level-hit" : ""}`}>
                <span className="atv2-level-label">TGT-1</span>
                <span className="atv2-level-value">
                  {d.tp1Hit && <span className="atv2-hit-icon atv2-hit-tp">✓</span>}
                  {fmt(d.target1)}
                </span>
              </div>
              <div className={`atv2-level-row${d.tp2Hit ? " atv2-level-hit" : ""}`}>
                <span className="atv2-level-label">TGT-2</span>
                <span className="atv2-level-value">
                  {d.tp2Hit && <span className="atv2-hit-icon atv2-hit-tp">✓</span>}
                  {fmt(d.target2)}
                </span>
              </div>
              <div className={`atv2-level-row${d.slHit ? " atv2-level-hit" : ""}`}>
                <span className="atv2-level-label">SL</span>
                <span className="atv2-level-value">
                  {d.slHit && <span className="atv2-hit-icon atv2-hit-sl">✗</span>}
                  {fmt(d.stopLoss)}
                </span>
              </div>
            </div>

            {/* ─── Divider ─── */}
            <div className="atv2-divider" />

            {/* ─── Status Chip (centered) ─── */}
            <div className="atv2-status-wrap">
              <span className={`atv2-status-chip ${statusClass}`}>{d.status}</span>
            </div>

            {/* ─── P/L (dual cells) ─── */}
            <div className="atv2-pl-row">
              <div className="atv2-pl-cell">
                <span className="atv2-pl-label">LIVE P/L</span>
                <span className={`atv2-pl-value ${d.livePL >= 0 ? "atv2-profit" : "atv2-loss"}`}>{fmtPL(d.livePL)}</span>
              </div>
              <div className="atv2-pl-cell">
                <span className="atv2-pl-label">BOOKED</span>
                <span className={`atv2-pl-value ${d.bookedPL >= 0 ? "atv2-profit" : "atv2-loss"}`}>{fmtPL(d.bookedPL)}</span>
              </div>
            </div>

          </>
        ) : (
          <>
            <span className="atv2-collapsed-title">Alpha Trade</span>
            <button className="atv2-toggle-btn atv2-toggle-btn--right" onClick={onToggleDashboard} title="Expand">▼</button>
          </>
        )}
      </div>
    </>
  );
};

export default AlphaTradeV2Overlay;
